import styled from '@emotion/styled';
import React, { useMemo } from 'react';

import { SemanticButton } from '@/features/common';

import { DSIconLoadingSpinner } from '@hundred5/design-system';
import aiSvg from './icons/ai.svg';
import asyncInterviewSvg from './icons/async-interview.svg';
import copySvg from './icons/copy.svg';
import customSvg from './icons/custom.svg';
import homeworkSvg from './icons/homework.svg';
import interviewSvg from './icons/interview.svg';
import librarySvg from './icons/library.svg';
import skillsTestSvg from './icons/skills.svg';
import templateSvg from './icons/template.svg';

interface CardButtonProps {
  title: string;
  description: string;
  secondaryTitle?: string;
  secondaryDescription?: string;
  active?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  tagText?: string;
  tagColor?: 'purple' | 'orange';
  tooltipText?: string;
  loading?: boolean;
}

export default function CardButton({
  title,
  description,
  secondaryTitle = 'Best for:',
  secondaryDescription,
  active = false,
  onClick = () => {},
  disabled = false,
  tagText,
  tagColor = 'purple',
  tooltipText,
  loading = false,
}: CardButtonProps) {
  const image = useMemo(() => {
    switch (title) {
      case 'Skills Test':
        return skillsTestSvg;
      case 'Interview':
        return interviewSvg;
      case 'Async Interview':
        return asyncInterviewSvg;
      case 'Homework Task':
        return homeworkSvg;
      case 'Choose template':
        return templateSvg;
      case 'Build custom assessment':
      case 'Build custom interview':
        return customSvg;
      case 'Copy from job':
        return copySvg;
      case 'Create with AI':
        return aiSvg;
      case 'Generate with AI':
        return aiSvg;
      case "Add from Toggl's library":
        return librarySvg;
      case 'Create custom question':
        return customSvg;
    }
    return skillsTestSvg;
  }, [title]);

  return (
    <div className="relative flex grow max-w-[340px] min-w-[220px]">
      <Container
        onClick={() => {
          if (!loading) {
            onClick();
          }
        }}
        disabled={disabled || loading}
        active={active}
        data-rh={tooltipText}
        data-rh-at="top"
        className="hover:shadow-raised-20"
      >
        {loading ? (
          <Loading size="36px" />
        ) : (
          <Image src={image} height={36} width={36} />
        )}
        <Title>{title}</Title>
        <Description>{description}</Description>
        {secondaryDescription && (
          <>
            <Separator />
            <SecondaryTitle>{secondaryTitle}</SecondaryTitle>
            <SecondaryDescription>{secondaryDescription}</SecondaryDescription>
          </>
        )}
      </Container>
      {tagText && <Tag backgroundColor={tagColor}>{tagText}</Tag>}
    </div>
  );
}
const Container = styled(SemanticButton)<{ active?: boolean }>`
  border: 1px solid
    ${({ theme, active }) =>
      active ? theme.colors.purple[40] : theme.colors.purple[10]};
  min-height: min-content;
  padding: 16px;
  display: flex;
  flex-direction: column;
  background: ${({ theme, active }) =>
    active ? theme.colors.purple[5] : theme.colors.white};
  text-align: left;
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 220px;

  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.purple[10]};
    background: ${({ theme }) => theme.colors.purple[5]};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.purple[5]};
    opacity: 0.4;
  }

  &:active {
    border: 1px solid ${({ theme }) => theme.colors.purple[40]};
  }
`;

const Title = styled.h3`
  color: ${({ theme }) => theme.colors.purple[100]};
  margin-bottom: 2px;
  margin-top: 0px;
  font-weight: 700;
`;

const Description = styled.div`
  color: ${({ theme }) => theme.colors.purple[70]};
  height: 42px;
`;

const SecondaryTitle = styled.div`
  color: ${({ theme }) => theme.colors.purple[100]};
  margin-bottom: 2px;
`;

const SecondaryDescription = styled.div`
  color: ${({ theme }) => theme.colors.purple[70]};
  height: 42px;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.purple[10]};
  margin-top: 12px;
  margin-bottom: 12px;
`;

const Image = styled.img`
  margin-bottom: 12px;
`;

// TODO: use the flow loader when we have access to the those components
const Loading = styled(DSIconLoadingSpinner)`
  margin-bottom: 12px;
`;

const Tag = styled.div<{ backgroundColor: 'orange' | 'purple' }>`
  position: absolute;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  top: 16px;
  right: 16px;
  height: 16px;
  padding: 0px 8px;
  border-radius: 8px;
  font-size: 10px;
  color: ${({ backgroundColor, theme }) =>
    backgroundColor === 'purple'
      ? theme.colors.white
      : theme.colors.purple[100]};
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor === 'purple'
      ? theme.colors.purple[90]
      : theme.colors.orange[20]};
  opacity: 1;
`;
